import { pruneForumPostSnapshots } from './postSnapshotCache';

const STORAGE_KEY = 'FORUM_POST_INTERACTION_PATCHES';
const MAX_PATCH_COUNT = 80;
let memoryPatchStore = {};

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const normalizeId = (value) => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const getPostId = (post = {}, fallback) => {
  if (post.postsId !== undefined && post.postsId !== null) return normalizeId(post.postsId);
  if (post.postId !== undefined && post.postId !== null) return normalizeId(post.postId);
  return normalizeId(fallback);
};

const readPatchStore = () => {
  if (!canUseSessionStorage()) return {};
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

const trimPatchStore = (store, maxCount = MAX_PATCH_COUNT) => {
  const keys = Object.keys(store);
  if (keys.length <= maxCount) return store;
  return keys
    .sort((a, b) => (store[b].updatedAt || 0) - (store[a].updatedAt || 0))
    .slice(0, maxCount)
    .reduce((next, key) => {
      next[key] = store[key];
      return next;
    }, {});
};

const safeSetPatchStore = (store, maxCount) => {
  if (!canUseSessionStorage()) return false;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimPatchStore(store, maxCount)));
    return true;
  } catch (error) {
    return false;
  }
};

const writePatchStore = (store) => {
  if (!canUseSessionStorage()) return;
  const retryCounts = [MAX_PATCH_COUNT, 40, 20, 10, 1];
  if (retryCounts.some((count) => safeSetPatchStore(store, count))) return;

  pruneForumPostSnapshots(undefined, 5);
  if (retryCounts.some((count) => safeSetPatchStore(store, count))) return;

  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Storage may be unavailable in private mode; current page state has still been updated.
  }
  safeSetPatchStore(store, 1);
};

const mergePatchStores = (...stores) =>
  trimPatchStore(
    stores.reduce((merged, store) => {
      Object.keys(store).forEach((key) => {
        const current = merged[key];
        const next = store[key];
        if (!current || (next.updatedAt || 0) >= (current.updatedAt || 0)) {
          merged[key] = next;
        }
      });
      return merged;
    }, {})
  );

const toCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

export const saveForumPostInteractionFromPost = (post = {}, fallbackPostId) => {
  const postId = getPostId(post, fallbackPostId);
  if (!postId) return;

  const patch = {};
  let hasPatch = false;
  if (typeof post.praise === 'boolean') {
    patch.praise = post.praise;
    hasPatch = true;
  }
  if (typeof post.collect === 'boolean') {
    patch.collect = post.collect;
    hasPatch = true;
  }
  if (post.praiseNum !== undefined && post.praiseNum !== null) {
    patch.praiseNum = toCount(post.praiseNum);
    hasPatch = true;
  }
  if (post.collectNum !== undefined && post.collectNum !== null) {
    patch.collectNum = toCount(post.collectNum);
    hasPatch = true;
  }
  if (post.commentNum !== undefined && post.commentNum !== null) {
    patch.commentNum = toCount(post.commentNum);
    hasPatch = true;
  }
  if (!hasPatch) return;

  const store = mergePatchStores(readPatchStore(), memoryPatchStore);
  store[postId] = {
    ...(store[postId] || { postId, updatedAt: 0 }),
    ...patch,
    postId,
    updatedAt: Date.now(),
  };
  memoryPatchStore = trimPatchStore(store);
  writePatchStore(store);
};

const patchForumPost = (post, patch) => {
  if (typeof patch.praise === 'boolean') post.praise = patch.praise;
  if (typeof patch.collect === 'boolean') post.collect = patch.collect;
  if (typeof patch.praiseNum === 'number') post.praiseNum = patch.praiseNum;
  if (typeof patch.collectNum === 'number') post.collectNum = patch.collectNum;
  if (typeof patch.commentNum === 'number') post.commentNum = patch.commentNum;
};

export const applyForumPostInteractionPatches = (lists = []) => {
  const store = mergePatchStores(readPatchStore(), memoryPatchStore);
  if (Object.keys(store).length === 0) return false;

  let applied = false;
  lists.forEach((posts) => {
    if (!Array.isArray(posts)) return;
    posts.forEach((post) => {
      const patch = store[getPostId(post)];
      if (!patch) return;
      patchForumPost(post, patch);
      applied = true;
    });
  });
  return applied;
};
